'use client';

import { cn } from '@/app/lib/utils';
import { FooterLogo, FooterColumns, FooterContact, FooterBottom } from './ui';
import { useFooter } from './lib/useFooter';
import { processFooterColumns, processContactInfo, processLegalInfo } from './lib/footer.utils';
import type { FooterData } from './interfaces';
import { footerVariants } from '@workspace/widgets/Footer/footer.variants';

export interface FooterProps {
  footerData?: FooterData;
}

export function Footer({ footerData: initialFooterData }: FooterProps) {
  const { footerData: fetchedFooterData } = useFooter();
  const typedFooter = (initialFooterData ?? fetchedFooterData) as FooterData | undefined;
  const { footer, container, contentGrid } = footerVariants();

  const columns = processFooterColumns(typedFooter?.footerColums);
  const contactInfo = processContactInfo(typedFooter?.footerContact);
  const legalProps = processLegalInfo(typedFooter?.legalInfo);

  const hasContent = columns.length > 0 || (contactInfo?.contactItems?.length ?? 0) > 0;

  return (
    <footer className={cn('relative w-full', footer())}>
      {hasContent && (
        <div className={cn('porta-container', container())}>
          <div className={cn('grid grid-cols-1 md:grid-cols-[auto_1fr_auto]', contentGrid())}>
            {/* Logo */}
            <div className="hidden md:block">
              <FooterLogo
                imageUrl={typedFooter?.footerLogo?.imageUrl}
                bynderLogo={typedFooter?.footerLogo?.bynderLogo}
                title={typedFooter?.footerLogo?.title}
                link={typedFooter?.footerLogo?.link}
              />
            </div>

            {/* Columns */}
            <div className="md:min-w-0 md:ml-gutter footer:ml-4xl">
              <FooterColumns columns={columns} />
            </div>

            {/* Contact */}
            <div className="md:justify-self-end md:ml-gutter footer:ml-3xl">
              <FooterContact contactInfo={contactInfo} socialLinks={[]} footerSocialLinks={typedFooter?.socialLinks} />
            </div>
          </div>
        </div>
      )}

      <FooterBottom
        copyrightText={legalProps.copyrightText}
        additionalText={legalProps.additionalText}
        legalLinks={legalProps.legalLinks}
      />
    </footer>
  );
}
